/**
 * Escapes *stray* MDX syntax in a markdown string so it can be parsed by the
 * strict MDX/micromark engine used by the editor without throwing
 * "Unexpected character … before name" errors.
 *
 * MDX treats a bare `<` as the start of a JSX tag and a bare `{` as a JS
 * expression. Prose like `revenue < 2%` or `Plan covers {2024} targets` makes
 * the parser choke. This escapes those stray occurrences to `\<` / `\{` (which
 * both micromark and `marked` render as a literal `<` / `{`), while leaving
 * real syntax untouched:
 *   - real HTML tags (`<div>`, `</a>`, `<!-- -->`) are preserved,
 *   - Gap directive attribute braces (`::GapFile{rawFileName="x"}`) are preserved,
 *   - code spans and fenced code blocks are preserved verbatim.
 *
 * The function is idempotent: already-escaped `\<` / `\{` are not escaped again.
 */
export const escapeStrayMdxSyntax = (markdown: string): string => {
   // 1. Protect code (fenced blocks + inline spans) — micromark never parses
   //    `<`/`{` inside them, and escaping there would corrupt code samples.
   //    Sentinels use underscores only, so the escape regexes below ignore them.
   const codeBlocks: string[] = []
   const withoutCode = markdown.replace(/(```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`)/g, match => {
      codeBlocks.push(match)
      return `__GAPCODE${codeBlocks.length - 1}__`
   })

   // 2. Protect directive attribute blocks (`:gap{…}`, `::GapFile{…}`, `:::note{…}`)
   //    so their braces survive the `{` escaping in step 4.
   const directives: string[] = []
   const withoutDirectives = withoutCode.replace(/:{1,3}[A-Za-z][\w-]*\{[^}]*\}/g, match => {
      directives.push(match)
      return `__GAPDIR${directives.length - 1}__`
   })

   // 3 & 4. Escape stray `<` (not a real tag start) and stray `{`.
   const escaped = withoutDirectives
      .replace(/(?<!\\)<(?![A-Za-z/!])/g, '\\<')
      .replace(/(?<!\\)\{/g, '\\{')

   // 5. Restore directives, then code blocks.
   const withDirectives = escaped.replace(/__GAPDIR(\d+)__/g, (_match, index) => directives[Number(index)])
   return withDirectives.replace(/__GAPCODE(\d+)__/g, (_match, index) => codeBlocks[Number(index)])
}
